import { httpClient } from './httpClient';
import {
  AccountKeyword,
  KeywordsListFilters,
  CreateKeywordData,
  UpdateKeywordData,
  ApiResponse,
} from '@/types/accountKeywords';

/**
 * Account Keywords Service
 * Handles all API calls to /api/v2/account-keywords
 */
class AccountKeywordsService {
  private static instance: AccountKeywordsService;
  private readonly baseUrl = '/api/v2/account-keywords';

  static getInstance(): AccountKeywordsService {
    if (!AccountKeywordsService.instance) {
      AccountKeywordsService.instance = new AccountKeywordsService();
    }
    return AccountKeywordsService.instance;
  }

  /**
   * Get list of keywords with optional filters
   */
  async getKeywords(filters?: KeywordsListFilters): Promise<AccountKeyword[]> {
    try {
      const params: Record<string, string | number> = {};

      if (filters?.account_id) {
        params.account_id = filters.account_id;
      }
      if (filters?.is_active !== undefined) {
        params.is_active = filters.is_active.toString();
      }
      if (filters?.search) {
        params.search = filters.search;
      }

      const response = await httpClient.get<ApiResponse<AccountKeyword[]>>(
        this.baseUrl,
        { params }
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch keywords');
      }

      return response.data;
    } catch (error) {
      console.error('[AccountKeywordsService] Error fetching keywords:', error);
      throw error;
    }
  }

  /**
   * Get a specific keyword by ID
   */
  async getKeyword(id: number): Promise<AccountKeyword> {
    try {
      const response = await httpClient.get<ApiResponse<AccountKeyword>>(`${this.baseUrl}/${id}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch keyword');
      }

      return response.data;
    } catch (error) {
      console.error(`[AccountKeywordsService] Error fetching keyword ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new keyword
   */
  async createKeyword(data: CreateKeywordData): Promise<AccountKeyword> {
    try {
      // Validation
      if (!data.account_id) {
        throw new Error('account_id is required');
      }

      if (!data.keyword || !data.keyword.trim()) {
        throw new Error('keyword is required');
      }

      const response = await httpClient.post<ApiResponse<AccountKeyword>>(this.baseUrl, data);

      if (!response.success) {
        throw new Error(response.message || 'Failed to create keyword');
      }

      return response.data;
    } catch (error) {
      console.error('[AccountKeywordsService] Error creating keyword:', error);
      throw error;
    }
  }

  /**
   * Update an existing keyword
   */
  async updateKeyword(id: number, data: UpdateKeywordData): Promise<AccountKeyword> {
    try {
      // Validation
      if (data.is_active === undefined) {
        throw new Error('is_active is required for updates');
      }

      const response = await httpClient.put<ApiResponse<AccountKeyword>>(`${this.baseUrl}/${id}`, data);

      if (!response.success) {
        throw new Error(response.message || 'Failed to update keyword');
      }

      return response.data;
    } catch (error) {
      console.error(`[AccountKeywordsService] Error updating keyword ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a keyword
   */
  async deleteKeyword(id: number): Promise<void> {
    try {
      const response = await httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete keyword');
      }
    } catch (error) {
      console.error(`[AccountKeywordsService] Error deleting keyword ${id}:`, error);
      throw error;
    }
  }
}

export const accountKeywordsService = AccountKeywordsService.getInstance();
